// The suite's shared header.
//
// REFERENCE: VehicleDebugger's `.site-header` in public/style.css and its markup in
// public/index.html. That tool is the canonical version; this is the MUI translation of it, and
// the EventManager keeps a near-identical copy at src/components/AppHeader.tsx. Geometry comes
// from `headerTokens` in the shared theme file so all three can be compared value by value.
//
// Layout: brand lockup on the left (logo · rule · product name over tagline), controls on the
// right (caller-supplied actions, then the theme button, then the language switcher).

import { AppBar, Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { DarkModeOutlined, LightModeOutlined } from '@mui/icons-material'
import type { MouseEvent, ReactNode } from 'react'
import { headerTokens, palette, type ThemeMode } from '../theme/appTheme'

export interface LanguageOption {
  code: string
  label: string
}

interface AppHeaderProps {
  logoSrc: string
  logoAlt: string
  productName: string
  tagline?: string
  mode: ThemeMode
  onToggleMode: () => void
  /**
   * Names the scheme clicking switches TO — "Dark mode" while light, "Light mode" while dark.
   * Used for both the tooltip and the aria-label, matching VehicleDebugger's
   * updateThemeToggleLabel(), which sets title and aria-label from the same string.
   */
  themeLabel: string
  /** Omit to hide the language switcher entirely. */
  language?: {
    label: string
    ariaLabel: string
    current: string
    options: LanguageOption[]
    onChange: (code: string) => void
  }
  /** Rendered left of the theme button — navigation, a user block, and so on. */
  actions?: ReactNode
  /** Called when the brand lockup is activated; omit to render it as static text. */
  onBrandClick?: () => void
  /**
   * Keep the header pinned while the document scrolls. Tools whose header carries navigation
   * want this; a tool without navigation can leave it off and let the header scroll away.
   */
  sticky?: boolean
}

export function AppHeader({
  logoSrc,
  logoAlt,
  productName,
  tagline,
  mode,
  onToggleMode,
  themeLabel,
  language,
  actions,
  onBrandClick,
  sticky = false,
}: AppHeaderProps) {
  const colors = palette[mode]

  const controlSx = {
    border: 1,
    borderColor: 'divider',
    borderRadius: headerTokens.controlRadius,
    backgroundColor: colors.surfaceMuted,
  }

  const brand = (
    <>
      <Box
        component="img"
        src={logoSrc}
        alt={logoAlt}
        sx={{
          height: { xs: headerTokens.logoHeightMobile, sm: headerTokens.logoHeight },
          width: 'auto',
          display: 'block',
          // The artwork is pure black, so inverting it yields the white variant for dark mode.
          filter: mode === 'dark' ? 'invert(1)' : 'none',
        }}
      />
      <Box
        sx={{
          // The rule between logo and name disappears once the header stacks, matching
          // VehicleDebugger's mobile rules.
          //
          // Longhand on purpose: the responsive `borderLeft: {xs, sm}` shorthand lands in a
          // media query that re-resets border-left-color to currentColor, which paints the
          // rule in the text colour instead of the divider colour.
          borderLeftStyle: 'solid',
          borderLeftWidth: { xs: 0, sm: '1px' },
          borderLeftColor: 'divider',
          pl: { xs: 0, sm: headerTokens.gap },
        }}
      >
        <Typography
          component="p"
          sx={{
            m: 0,
            fontSize: headerTokens.nameFontSize,
            fontWeight: headerTokens.nameFontWeight,
            letterSpacing: headerTokens.nameLetterSpacing,
          }}
        >
          {productName}
        </Typography>
        {tagline && (
          <Typography
            component="p"
            sx={{
              margin: 0,
              marginTop: headerTokens.taglineMarginTop,
              fontSize: headerTokens.taglineFontSize,
              // Set through sx rather than the `color` prop: the prop loses to the ancestor
              // colour that AppBar/Toolbar establishes.
              color: 'text.secondary',
            }}
          >
            {tagline}
          </Typography>
        )}
      </Box>
    </>
  )

  return (
    <AppBar
      position={sticky ? 'sticky' : 'static'}
      elevation={0}
      sx={{
        top: sticky ? 0 : undefined,
        backgroundColor: 'background.paper',
        color: 'text.primary',
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: colors.headerShadow,
        // Padding on the bar, max-width on the row inside it — the same split as
        // VehicleDebugger's `.site-header` / `.header-inner`. Putting both on one element
        // makes the content 2 * paddingX narrower than the 1040px row and shifts it right.
        px: headerTokens.paddingX,
        py: headerTokens.paddingY,
      }}
    >
      {/* Plain Box, not Toolbar: MUI's Toolbar sets its height through a mixin inside a media
          query, which outranks an sx `minHeight` and would unpin the row height. */}
      <Box
        sx={{
          maxWidth: headerTokens.maxWidth,
          width: '100%',
          mx: 'auto',
          // Pinned so the header keeps one height across the suite no matter what the actions
          // slot holds — see headerTokens.contentMinHeight.
          minHeight: headerTokens.contentMinHeight,
          display: 'flex',
          gap: headerTokens.gap,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
        }}
      >
        {onBrandClick ? (
          <Box
            component="a"
            href="/"
            onClick={(event: MouseEvent) => {
              event.preventDefault()
              onBrandClick()
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: headerTokens.gap,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            {brand}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: headerTokens.gap }}>{brand}</Box>
        )}

        <Stack
          direction="row"
          sx={{
            ml: { sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: headerTokens.actionsGap,
            flexWrap: 'wrap',
          }}
        >
          {actions}

          <Tooltip title={themeLabel}>
            <IconButton
              onClick={onToggleMode}
              aria-label={themeLabel}
              aria-pressed={mode === 'dark'}
              sx={{
                ...controlSx,
                width: headerTokens.controlSize,
                height: headerTokens.controlSize,
                p: 0,
                color: 'text.primary',
                flexShrink: 0,
                '&:hover': {
                  borderColor: colors.borderStrong,
                  backgroundColor: 'background.paper',
                },
              }}
            >
              {/* Shows the CURRENT scheme, matching VehicleDebugger's .theme-icon rules
                  (sun in light mode, moon in dark). The tooltip states what clicking does. */}
              {mode === 'light' ? (
                <LightModeOutlined sx={{ fontSize: '1.1rem' }} />
              ) : (
                <DarkModeOutlined sx={{ fontSize: '1.1rem' }} />
              )}
            </IconButton>
          </Tooltip>

          {language && (
            <Box
              role="group"
              aria-label={language.ariaLabel}
              sx={{
                ...controlSx,
                display: 'flex',
                alignItems: 'center',
                gap: headerTokens.switcherGap,
                padding: headerTokens.switcherPadding,
                flexShrink: 0,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: headerTokens.controlFontSize,
                  marginRight: headerTokens.switcherLabelGap,
                  // Through sx, not the `color` prop: the prop loses to the ancestor colour
                  // AppBar establishes, which would render the label in full text colour.
                  color: 'text.secondary',
                }}
              >
                {language.label}
              </Typography>
              {language.options.map((option) => {
                const active = option.code === language.current
                return (
                  <Box
                    key={option.code}
                    component="button"
                    type="button"
                    aria-pressed={active}
                    onClick={() => language.onChange(option.code)}
                    sx={{
                      padding: headerTokens.chipPadding,
                      border: 1,
                      borderColor: active ? 'primary.main' : 'divider',
                      borderRadius: headerTokens.chipRadius,
                      backgroundColor: active ? 'primary.main' : 'background.paper',
                      color: active ? 'primary.contrastText' : 'text.secondary',
                      fontFamily: 'inherit',
                      fontSize: headerTokens.controlFontSize,
                      fontWeight: 600,
                      // `normal`, matching the plain <button> in VehicleDebugger; MUI's
                      // inherited line-height would make the chip a different height.
                      lineHeight: 'normal',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
                      '&:hover': active
                        ? {}
                        : { borderColor: colors.borderStrong, color: 'text.primary' },
                    }}
                  >
                    {option.label}
                  </Box>
                )
              })}
            </Box>
          )}
        </Stack>
      </Box>
    </AppBar>
  )
}
